import React, { useState, useRef, useEffect } from 'react';
import {
  IconButton,
  Autocomplete,
  TextField,
  Box,
  InputAdornment
} from '@mui/material';
import { MyLocationRounded } from '@mui/icons-material';

const ExpandableAutoComplete = ({
  options = [],
  value,
  onChange,
  disabled = false,
  width = 250,
  ...autocompleteProps
}) => {
  const [expanded, setExpanded] = useState(false);
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (expanded && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 300);
    }
  }, [expanded]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const popperElement = document
        .querySelector('[role="listbox"]')
        ?.closest('.MuiPopper-root');

      const isClickInContainer = containerRef.current?.contains(event.target);
      const isClickInPopper = popperElement?.contains(event.target);

      if (!isClickInContainer && !isClickInPopper) {
        setExpanded(false);
      }
    };

    if (expanded) {
      setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside, true);
      }, 0);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
    };
  }, [expanded]);

  const handleIconClick = () => {
    if (!disabled) {
      setExpanded(true);
    }
  };

  const handleChange = (event, newValue) => {
    onChange?.(event, newValue);
  };

  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: expanded ? width : 34,
        height: 34,
        transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        borderRadius: 1,
        flexShrink: 0
      }}
    >
      <IconButton
        size="small"
        onClick={handleIconClick}
        disabled={disabled}
        sx={{
          position: 'absolute',
          left: 0,
          zIndex: 2,
          opacity: expanded ? 0 : 1,
          transition: 'opacity 0.2s ease-in-out',
          pointerEvents: expanded ? 'none' : 'auto'
        }}
      >
        <MyLocationRounded sx={{ fontSize: 22, color: '#3d4551' }} />
      </IconButton>
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          width: '100%',
          opacity: expanded ? 1 : 0,
          transition: 'opacity 0.3s ease-in-out',
          pointerEvents: expanded ? 'auto' : 'none'
        }}
      >
        <Autocomplete
          options={options}
          value={value}
          onChange={handleChange}
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          sx={{ width: '100%' }}
          renderInput={(params) => (
            <TextField
              {...params}
              inputRef={inputRef}
              placeholder={options[0]?.label}
              size="small"
              slotProps={{
                ...params.slotProps,
                input: {
                  ...params.slotProps.input,
                  startAdornment: (
                    <>
                      <InputAdornment position="start" sx={{ px: 1 }}>
                        <MyLocationRounded fontSize="medium" color="action" />
                      </InputAdornment>
                      {params.slotProps.input.startAdornment}
                    </>
                  )
                }
              }}
            />
          )}
          {...autocompleteProps}
        />
      </Box>
    </Box>
  );
};

export default ExpandableAutoComplete;
